import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

import usePartnerService from "../../../api/payments/partners/partner.service";
import { Partner } from "../../../api/payments/partners/partner.responses";
import { Button } from "../../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/Table";
import { ActiveStatusBadge } from "../shared/ActiveStatusBadge";
import { DeactivateDialog } from "../shared/DeactivateDialog";
import { TabHeader } from "../shared/TabHeader";
import PartnerForm from "./PartnerForm";

const createSchema = z.object({
  name: z.string().min(1, "Required"),
  contactEmail: z.string().email("Invalid email").optional().or(z.literal("")),
});

const editSchema = z.object({
  name: z.string().min(1, "Required"),
  contactEmail: z.string().email("Invalid email").optional().or(z.literal("")),
  isActive: z.boolean(),
});

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;

function PartnersTab() {
  const service = usePartnerService();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPartner, setEditPartner] = useState<Partner | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", contactEmail: "" },
  });

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: "", contactEmail: "", isActive: true },
  });

  const fetchPartners = () => {
    service
      .listPartners()
      .then((res) => setPartners(res.data))
      .catch(() => toast.error("Failed to load partners"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleCreate = async (values: CreateFormValues) => {
    setSubmitting(true);
    try {
      await service.createPartner({
        name: values.name,
        contactEmail: values.contactEmail || undefined,
      });
      toast.success("Partner created");
      setCreateOpen(false);
      createForm.reset();
      fetchPartners();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to create partner");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (partner: Partner) => {
    setEditPartner(partner);
    editForm.reset({
      name: partner.name,
      contactEmail: partner.contactEmail ?? "",
      isActive: partner.isActive,
    });
  };

  const handleEdit = async (values: EditFormValues) => {
    if (!editPartner) return;
    setSubmitting(true);
    try {
      await service.updatePartner(editPartner.id, {
        name: values.name,
        contactEmail: values.contactEmail || null,
        isActive: values.isActive,
      });
      toast.success("Partner updated");
      setEditPartner(null);
      fetchPartners();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to update partner");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteId) return;
    setSubmitting(true);
    try {
      await service.deactivatePartner(deleteId);
      toast.success("Partner deactivated");
      setDeleteId(null);
      fetchPartners();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to deactivate partner"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <TabHeader title="Partners">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Partner
        </Button>
      </TabHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : partners.length === 0 ? (
        <p className="text-sm text-muted-foreground">No partners yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.map((partner) => (
              <TableRow key={partner.id}>
                <TableCell className="font-medium">{partner.name}</TableCell>
                <TableCell className="text-muted-foreground">
                  {partner.contactEmail ?? "—"}
                </TableCell>
                <TableCell>
                  <ActiveStatusBadge isActive={partner.isActive} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditOpen(partner)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {partner.isActive && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(partner.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Partner</DialogTitle>
          </DialogHeader>
          <PartnerForm
            form={createForm}
            onSubmit={handleCreate}
            onCancel={() => setCreateOpen(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editPartner}
        onOpenChange={(o) => !o && setEditPartner(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
          </DialogHeader>
          <PartnerForm
            form={editForm}
            onSubmit={handleEdit}
            onCancel={() => setEditPartner(null)}
            isEdit
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      <DeactivateDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Deactivate Partner"
        description="This will deactivate the partner. Their promo codes will remain but won't be linked to an active partner."
        onConfirm={handleDeactivate}
        submitting={submitting}
      />
    </div>
  );
}

export default PartnersTab;
